import React from 'react';
import { ArrowRightIcon, SkipForwardIcon } from 'lucide-react';
import { useDemo } from '../../contexts/DemoContext';
import { ScreenContainer } from '../story/ScreenContainer';
import { ScreenHeading } from '../story/ScreenHeading';
import { ClueButton } from '../story/ClueButton';
import { DialogueList } from '../story/DialogueList';
import { BrainWorld } from '../story/BrainWorld';
import { ActionRow } from '../story/ActionRow';
import { Button } from '../story/Button';
import { SceneIllustration } from '../art/SceneIllustration';
import { SavannaScene } from '../art/SavannaScene';
import { clues, cluesBrainWorld } from '../../data/savanna';
import type { ClueId } from '../../types/demo';
const SCENE_TEXT = 'The watering hole. Six things to look at: Monkey at the water’s edge, Meerkats on the wall, Panther low near the grass, the foggy side-water under the roots, the grass by the old pipe, and Amara near the acacia.';
export function CluesScreen() {
  const {
    state,
    dispatch,
    world,
    announce
  } = useDemo();
  const active = clues.find(clue => clue.id === state.activeClue) ?? null;
  const view = (id: ClueId) => {
    dispatch({
      type: 'VIEW_CLUE',
      clue: id
    });
    const clue = clues.find(item => item.id === id);
    if (clue) announce(`${clue.name}. ${clue.observation}`);
  };
  return <ScreenContainer data-id="element-127" data-mp="36phln3v9vcz8">
      <ScreenHeading eyebrow="Explore" data-id="element-128" data-mp="36oy0zfe8mahi">Notice before certainty.</ScreenHeading>
      <p className="mt-3 max-w-prose" data-id="element-129" data-mp="2syktfe59cxsp">Choose a clue. You can look at all six or move on.</p>

      <div className="mt-6" data-id="element-130" data-mp="2xq2t7yvjosc3">
        <SceneIllustration description={SCENE_TEXT} quietTitle="Watering hole" data-id="element-131" data-mp="2xqcl5s05h9kf">
          <div className="relative" data-id="element-132" data-mp="2j1py144a7din">
            <SavannaScene world={world} label={SCENE_TEXT} highlight={state.activeClue} showLabels={false} data-id="element-133" data-mp="2iyllnjgnxbgw" />
            {clues.map(clue => <ClueButton key={clue.id} variant="hotspot" name={clue.name} viewed={state.viewedClues.includes(clue.id)} active={state.activeClue === clue.id} onClick={() => view(clue.id)} style={{
            left: `${clue.position.x}%`,
            top: `${clue.position.y}%`
          }} data-id="element-134" data-mp="29zgqmn4b809h" />)}
          </div>
        </SceneIllustration>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,320px)_1fr]" data-id="element-135" data-mp="aqywt0z1tvhn">
        <div data-id="element-136" data-mp="6srcxx9age28">
          <h2 className="font-display text-xl font-semibold text-forest" data-id="element-137" data-mp="6taz5q5z1om2">All clues</h2>
          <p className="mt-1 text-sm text-ink/80" data-id="element-138" data-mp="13h75m27p33jk">
            {state.viewedClues.length === 0 ? 'None viewed yet.' : `${state.viewedClues.length} of 6 viewed.`}
          </p>
          <ul className="mt-3 flex flex-col gap-2" data-id="element-139" data-mp="ug9904zl5yb0">
            {clues.map(clue => <li key={clue.id} data-id="element-140" data-mp="pihcexnzxo0h">
                <ClueButton name={clue.name} viewed={state.viewedClues.includes(clue.id)} active={state.activeClue === clue.id} onClick={() => view(clue.id)} data-id="element-141" data-mp="phfvickau7au" />
              </li>)}
          </ul>
        </div>
        <div className="flex flex-col gap-6" data-id="element-142" data-mp="1prz54m2ryd5p">
          <section aria-label="Clue details" className="min-h-[200px] rounded-2xl border border-line bg-paper p-5 hc-strong" data-id="element-143" data-mp="1uopj93v0qyoh">
            {active ? <div key={active.id} className="anim-enter" data-id="element-144" data-mp="23hj3zgt8iq9q">
                <h3 className="font-display text-2xl font-semibold text-forest" data-id="element-145" data-mp="23iu4n7rdvhwm">{active.name}</h3>
                {active.lines.length > 0 && <DialogueList lines={active.lines} className="mt-4" data-id="element-146" data-mp="28izy8th7rehc" />}
                <p className="mt-4 rounded-xl bg-sand px-4 py-3" data-id="element-147" data-mp="2de5no29trba6">
                  <span className="font-bold text-forest" data-id="element-148" data-mp="2iejvbj7ikj7o">What you notice: </span>
                  {active.observation}
                </p>
              </div> : <p className="text-ink/80" data-id="element-149" data-mp="o1251ofq588y">Pick a clue from the scene or the list to look closer.</p>}
          </section>
          <BrainWorld text={cluesBrainWorld} data-id="element-150" data-mp="s3f7o3z6cq1i" />
        </div>
      </div>

      <ActionRow onBack={() => dispatch({
      type: 'BACK'
    })} data-id="element-151" data-mp="3sx26k5ewha7x">
        <Button variant="secondary" onClick={() => dispatch({
        type: 'GO',
        screen: 'action',
        recap: 'You skipped the sorting. Your Field Guide tools stay the same. You can sort later from Story steps.'
      })} icon={<SkipForwardIcon size={18} aria-hidden="true" data-id="element-153" data-mp="1phajsyc3he6" />} data-id="element-152" data-mp="3szoe451sjpq5">
          Skip to a next step
        </Button>
        <Button onClick={() => dispatch({
        type: 'GO',
        screen: 'sorting'
      })} iconAfter={<ArrowRightIcon size={18} aria-hidden="true" data-id="element-155" data-mp="3jwx1i1x6inwx" />} data-id="element-154" data-mp="3580qbvztsus3">
          Sort the signals
        </Button>
      </ActionRow>
    </ScreenContainer>;
}